
import { Icon } from '@/components/ui/Icon'
import { formatVa, formatW } from '@/lib/power'
import { coverageSentence, type BuiltPowerPlan, type PowerPlanSettings } from '@/lib/power-plan'
import { cx, glass, segment } from '@/lib/ui'

type Confidence = 'known' | 'estimated' | 'unknown'

/**
 * The answer, at the top, always visible.
 *
 * Connected and Demand sit next to each other under different labels because
 * they are the two numbers people conflate: one sizes the copper, the other
 * sizes the bill. REQUIRED is the only figure set large, because it is the one
 * that goes on the order.
 *
 * Coverage and Scope live here rather than in a side panel: both are statements
 * about whether the big number can be trusted, so a screenshot of the headline
 * carries them with it.
 */
export function PowerResultBar({
  built,
  settings,
  confidenceFilter,
  onConfidenceFilter,
}: {
  built: BuiltPowerPlan
  settings: PowerPlanSettings
  confidenceFilter: Confidence | null
  onConfidenceFilter: (next: Confidence | null) => void
}) {
  const { totals, coverage, legs, balance } = built
  const threePhase = legs.length > 0
  const worstLeg = legs.reduce<(typeof legs)[number] | null>(
    (worst, leg) => (worst === null || leg.ampsConnected > worst.ampsConnected ? leg : worst),
    null,
  )
  const estimated = coverage.derived
  const known = coverage.known

  const toggle = (next: Confidence) => onConfidenceFilter(confidenceFilter === next ? null : next)

  return (
    <section className={cx(glass, 'sticky top-3 z-20 px-5 py-4')} aria-label="Power result">
      <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
        <Readout label="Connected" hint="peak — sizes circuits, distro, tie-in">
          {formatVa(totals.vaConnected)}
        </Readout>
        <Readout label="Demand" hint="typical, after diversity">
          {formatVa(totals.vaDemand)}
        </Readout>

        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted">Required</p>
          <p className="text-2xl font-semibold tabular-nums tracking-tight">
            {formatVa(totals.vaRequired)}
          </p>
          {/* Set at the same size as the number it qualifies, directly beneath
              it, so the caveat cannot be cropped away from the total. */}
          <p className="text-[11px] text-muted">
            demand + {settings.headroomPct}% headroom
            {totals.suggestedMcb != null && ` · ${totals.suggestedMcb} A device`}
          </p>
        </div>

        {threePhase ? (
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted">Worst leg</p>
            <p className="text-sm font-medium tabular-nums">
              {worstLeg ? `${worstLeg.leg} · ${worstLeg.ampsConnected.toFixed(1)} A` : '—'}
            </p>
            <div className="mt-1.5 flex items-end gap-1" aria-hidden>
              {legs.map((leg) => {
                const peak = worstLeg?.ampsConnected || 1
                return (
                  <span
                    key={leg.leg}
                    className="w-6 rounded-sm bg-[var(--ink)]/70"
                    style={{ height: `${Math.max(3, (leg.ampsConnected / peak) * 22)}px` }}
                  />
                )
              })}
            </div>
            {balance && (
              <p className="mt-1 text-[11px] text-muted tabular-nums">
                NEMA {balance.nemaPct.toFixed(1)}% · spread {balance.spreadPct.toFixed(1)}%
              </p>
            )}
          </div>
        ) : (
          /* Single phase: one total-amps figure. On three phase this element is
             absent entirely rather than showing a sum no breaker ever sees. */
          <Readout label="Total" hint={`at ${settings.voltage} V`}>
            {totals.ampsConnected.toFixed(1)} A
          </Readout>
        )}
      </div>

      {totals.genset && (
        <p className={cx('mt-3 text-[11px]', totals.genset.verdict === 'OVER' ? 'text-danger' : 'text-muted')}>
          {totals.genset.binds === 'kW' ? (
            <>
              The engine binds: {totals.genset.kvaUtil.toFixed(0)}% of the set&rsquo;s kVA but{' '}
              <strong className="font-medium">{totals.genset.kwUtil.toFixed(0)}% of its kW</strong> (
              {formatW(totals.wRequired)}).
            </>
          ) : (
            <>
              The alternator binds: {totals.genset.kvaUtil.toFixed(0)}% of the set&rsquo;s kVA,{' '}
              {totals.genset.kwUtil.toFixed(0)}% of its kW.
            </>
          )}
          {totals.genset.note && ` ${totals.genset.note}`}
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-[var(--border)] pt-3">
        <button
          type="button"
          onClick={() => toggle('known')}
          aria-pressed={confidenceFilter === 'known'}
          className={cx(segment.base, confidenceFilter === 'known' ? segment.on : segment.off)}
        >
          <Dot tone="emerald" /> {known} published
        </button>
        <button
          type="button"
          onClick={() => toggle('estimated')}
          aria-pressed={confidenceFilter === 'estimated'}
          className={cx(segment.base, confidenceFilter === 'estimated' ? segment.on : segment.off)}
        >
          <Dot tone="amber" /> {estimated} estimated
        </button>
        <button
          type="button"
          onClick={() => toggle('unknown')}
          aria-pressed={confidenceFilter === 'unknown'}
          className={cx(segment.base, confidenceFilter === 'unknown' ? segment.on : segment.off)}
        >
          <Dot tone="grey" /> {coverage.unknown} unknown
        </button>
      </div>

      <p
        className={cx(
          'mt-2 text-[11px]',
          coverage.unknown > 0 ? 'font-medium text-warning' : 'text-muted',
        )}
      >
        {coverageSentence(coverage)}
      </p>

      <p className="mt-2 flex items-start gap-1.5 text-[11px] text-muted">
        <Icon name="eye" className="mt-0.5 h-3 w-3 shrink-0" />
        <span>
          Scope: this plan totals only the rows on it. Lighting, LED wall, monitors, audio PA and
          house HVAC are not modelled unless you added them.
        </span>
      </p>
    </section>
  )
}

function Readout({
  label,
  hint,
  children,
}: {
  label: string
  hint: string
  children: React.ReactNode
}) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted">{label}</p>
      <p className="text-sm font-medium tabular-nums">{children}</p>
      <p className="text-[11px] text-muted">{hint}</p>
    </div>
  )
}

function Dot({ tone }: { tone: 'emerald' | 'amber' | 'grey' }) {
  const fill =
    tone === 'emerald' ? 'bg-success' : tone === 'amber' ? 'bg-warning' : 'bg-[var(--border-strong)]'
  return <span aria-hidden className={cx('h-1.5 w-1.5 rounded-full', fill)} />
}
